import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play, ArrowRight, Ruler } from "lucide-react";
import { api, errMsg, orthoUrl, compareSrc } from "@/lib/api";
import { PageTitle, Prio, StatusBadge } from "@/components/StatusBadge";

const Kpi = ({ label, value, accent, testId, delay }) => (
  <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5 fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">{label}</div>
    <div className={`font-display text-4xl font-bold mt-2 ${accent || ""}`}>{value ?? "–"}</div>
  </div>
);

const MeldingCard = ({ m, i }) => {
  const nav = useNavigate();
  const main = m.objecten?.[0];
  return (
    <button onClick={() => nav(`/meldingen/${m.id}`)} data-testid={`dashboard-melding-${m.id}`}
      className="text-left w-full min-w-0 flex gap-4 border border-slate-800 bg-[#111827]/80 hover:border-blue-600/60 transition-colors rounded-lg p-3 fade-up" style={{ animationDelay: `${i * 40}ms` }}>
      <img src={m.upload_id ? compareSrc(m.address_id, { id: m.analysis_id, upload_id: m.upload_id }) : orthoUrl(m.address_id, m.compare_year)} alt="" className="h-24 w-24 rounded object-cover shrink-0 bg-slate-900" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center justify-between gap-2"><StatusBadge status={m.status} /><Prio value={m.prioriteit} /></div>
        <div className="font-medium truncate">{m.weergavenaam}</div>
        <div className="font-mono text-[11px] text-slate-500">{m.perceel_aanduiding} · {m.baseline_year}→{m.upload_id ? m.compare_label : m.compare_year}</div>
        {main && (
          <div className="flex items-center gap-1.5 text-xs text-amber-300">
            <Ruler className="h-3.5 w-3.5" /><span className="capitalize">{main.type}</span> · ca. {main.lengte_m}×{main.breedte_m} m
            {m.objecten.length > 1 && <span className="text-slate-500">+{m.objecten.length - 1}</span>}
          </div>
        )}
      </div>
    </button>
  );
};

export default function Dashboard() {
  const qc = useQueryClient();
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/stats").then((r) => r.data) });
  const { data: open = [] } = useQuery({ queryKey: ["meldingen", "open"], queryFn: () => api.get("/meldingen", { params: { open_only: true } }).then((r) => r.data) });
  const ps = stats?.per_status || {};
  const nieuw = open.filter((m) => m.status === "nieuw");
  const bezig = open.filter((m) => m.status !== "nieuw");

  const runAll = async () => {
    try {
      const { data } = await api.post("/analyze/batch", {});
      toast.success(`Analyse gestart voor ${data.total} adressen`);
      qc.invalidateQueries({ queryKey: ["jobs"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <PageTitle eyebrow="Werkvoorraad" title="Wat moet er vandaag beoordeeld worden?">
        <button data-testid="run-batch-analysis-btn" onClick={runAll} className="flex items-center gap-2 h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-500 transition-colors text-sm font-medium">
          <Play className="h-4 w-4" /> Analyse alle percelen
        </button>
      </PageTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Kpi label="Adressen (BAG)" value={stats?.adressen} testId="kpi-adressen" delay={0} />
        <Kpi label="Geanalyseerd" value={stats?.geanalyseerd} testId="kpi-geanalyseerd" delay={60} />
        <Kpi label="Te beoordelen" value={ps.nieuw} accent="text-amber-400" testId="kpi-nieuw" delay={120} />
        <Kpi label="Doorgezet handhaving" value={ps.doorgezet} accent="text-rose-400" testId="kpi-doorgezet" delay={180} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-8 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Nieuwe meldingen <span className="font-mono text-sm text-slate-500">({nieuw.length})</span></h2>
            <Link to="/meldingen" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid="dashboard-all-meldingen-link">Alle meldingen <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          {nieuw.length === 0 ? (
            <div className="border border-dashed border-slate-800 rounded-lg p-10 text-center text-slate-500 text-sm" data-testid="dashboard-empty">
              Geen nieuwe meldingen. Importeer adressen en start een analyse om wijzigingen te detecteren.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{nieuw.map((m, i) => <MeldingCard key={m.id} m={m} i={i} />)}</div>
          )}
        </section>
        <aside className="lg:col-span-4 space-y-6">
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5">
            <h3 className="font-display font-semibold mb-4">Status overzicht</h3>
            <div className="space-y-2.5">
              {["inspecteren", "bezoeken", "doorgezet", "vergund", "goedgekeurd", "afgewezen", "afgehandeld"].map((s) => (
                <Link key={s} to={`/meldingen?status=${s}`} className="flex items-center justify-between hover:bg-slate-800/40 rounded px-1 py-0.5" data-testid={`status-count-${s}`}>
                  <StatusBadge status={s} /><span className="font-mono text-sm">{ps[s] ?? 0}</span>
                </Link>
              ))}
            </div>
          </div>
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5">
            <h3 className="font-display font-semibold mb-3">In behandeling</h3>
            {bezig.length === 0 && <p className="text-xs text-slate-500">Niets in behandeling.</p>}
            {bezig.slice(0, 8).map((m) => (
              <Link key={m.id} to={`/meldingen/${m.id}`} className="flex items-center justify-between gap-2 py-2 border-b border-slate-800/60 last:border-0 text-sm hover:text-blue-300">
                <span className="truncate">{m.weergavenaam}</span><StatusBadge status={m.status} />
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
